import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@bcc/components/ui/table";
import type { Invoice } from "@bcc/lib/types/invoice";
import { ArrowUpDown, Eye } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setInvoiceTableContainerTestId,
  setInvoiceTableEmptyMessageTestId,
  setInvoiceTableHeaderTestId,
  setInvoiceTableRowTestId,
  setInvoiceTableStatusBadgeTestId,
} from "./invoice-table.utils";

interface InvoiceTableProps {
  invoices: Invoice[];
  onInvoiceClick: (invoice: Invoice) => void;
  showPaymentStatus?: boolean;
  testId?: string;
}

type SortField = "id" | "amount" | "dueDate" | "status";
type SortDirection = "asc" | "desc";

export function InvoiceTable({
  invoices,
  onInvoiceClick,
  showPaymentStatus = false,
  testId,
}: InvoiceTableProps) {
  const [sortField, setSortField] = useState<SortField>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const { t } = useTranslation();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortButton = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className="h-8 -ml-3 hover:bg-transparent"
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  const sortedInvoices = [...invoices].sort((a, b) => {
    let compareA: string | number = "";
    let compareB: string | number = "";

    switch (sortField) {
      case "id":
        compareA = a.id;
        compareB = b.id;
        break;
      case "amount":
        compareA = a.amountEUR;
        compareB = b.amountEUR;
        break;
      case "status":
        compareA = a.status;
        compareB = b.status;
        break;
      case "dueDate":
        compareA = new Date(a.dueDate).getTime();
        compareB = new Date(b.dueDate).getTime();
        break;
    }

    if (compareA < compareB) return sortDirection === "asc" ? -1 : 1;
    if (compareA > compareB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const formatAmount = (amount: number) =>
    `€${amount.toLocaleString("en-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case "Paid":
        return "bg-green-500 text-white hover:bg-green-600";
      case "Unpaid":
        return "bg-yellow-500 text-white hover:bg-yellow-600";
      case "Overdue":
        return "bg-red-500 text-white hover:bg-red-600";
      case "Unposted":
        return "bg-gray-500 text-white hover:bg-gray-600";
      default:
        return "bg-gray-500 text-white hover:bg-gray-600";
    }
  };

  const getPaymentStatusVariant = (
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <div
      data-testid={testId ?? setInvoiceTableContainerTestId()}
      className="rounded-lg border border-border bg-card"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead data-testid={setInvoiceTableHeaderTestId("invoiceId")}>
              <SortButton field="id">
                {t("finance.invoiceTable.columns.invoiceId")}
              </SortButton>
            </TableHead>
            <TableHead data-testid={setInvoiceTableHeaderTestId("amount")}>
              <SortButton field="amount">
                {t("finance.invoiceTable.columns.amount")}
              </SortButton>
            </TableHead>
            <TableHead data-testid={setInvoiceTableHeaderTestId("status")}>
              <SortButton field="status">
                {t("finance.invoiceTable.columns.status")}
              </SortButton>
            </TableHead>
            <TableHead data-testid={setInvoiceTableHeaderTestId("payDueDate")}>
              {t("finance.invoiceTable.columns.payDueDate")}
            </TableHead>
            <TableHead data-testid={setInvoiceTableHeaderTestId("totalOrders")}>
              {t("finance.invoiceTable.columns.totalOrders")}
            </TableHead>
            {showPaymentStatus && (
              <TableHead
                data-testid={setInvoiceTableHeaderTestId("paymentStatus")}
              >
                {t("finance.invoiceTable.columns.paymentStatus")}
              </TableHead>
            )}
            <TableHead
              className="text-right"
              data-testid={setInvoiceTableHeaderTestId("action")}
            >
              {t("finance.invoiceTable.columns.action")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedInvoices.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showPaymentStatus ? 7 : 6}
                className="text-center text-muted-foreground"
                data-testid={setInvoiceTableEmptyMessageTestId()}
              >
                {t("finance.invoiceTable.noInvoices")}
              </TableCell>
            </TableRow>
          ) : (
            sortedInvoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onInvoiceClick(invoice)}
                data-testid={setInvoiceTableRowTestId(invoice.id)}
              >
                <TableCell className="font-medium">{invoice.id}</TableCell>
                <TableCell>{formatAmount(invoice.amountEUR)}</TableCell>
                <TableCell>
                  <Badge
                    className={getStatusBadgeClass(invoice.status)}
                    data-testid={setInvoiceTableStatusBadgeTestId(invoice.id)}
                  >
                    {invoice.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {invoice.payDate && (
                      <div className="text-xs text-muted-foreground">
                        {t("finance.invoiceTable.paid")}:{" "}
                        {formatDate(invoice.payDate)}
                      </div>
                    )}
                    <div className="text-xs">
                      {t("finance.invoiceTable.due")}:{" "}
                      {formatDate(invoice.dueDate)}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{invoice.ordersCount}</TableCell>
                {showPaymentStatus && (
                  <TableCell>
                    {invoice.paymentStatus ? (
                      <Badge
                        variant={getPaymentStatusVariant(invoice.paymentStatus)}
                      >
                        {invoice.paymentStatus}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onInvoiceClick(invoice);
                    }}
                    data-testid={`view-invoice-${invoice.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
